/**
 * PostHog Event Constants and Tracking Helpers
 *
 * Centralized event definitions for consistent analytics tracking across the Easel platform.
 * Use these constants and helpers to ensure event names and properties are consistent.
 */

import type { PostHog } from 'posthog-js'

// =============================================================================
// Event Name Constants
// =============================================================================

/**
 * User lifecycle events
 */
export const UserEvents = {
  SIGNED_UP: 'user_signed_up',
  SIGNED_IN: 'user_signed_in',
  SIGNED_OUT: 'user_signed_out',
  PROFILE_UPDATED: 'profile_updated',
} as const

/**
 * Artwork management events
 */
export const ArtworkEvents = {
  UPLOADED: 'artwork_uploaded',
  PUBLISHED: 'artwork_published',
  UNPUBLISHED: 'artwork_unpublished',
  VIEWED: 'artwork_viewed',
  DELETED: 'artwork_deleted',
  TAGS_UPDATED: 'artwork_tags_updated',
  COMMISSIONS_UPDATED: 'artwork_commissions_updated',
} as const

/**
 * Easel Portfolio events
 */
export const EaselEvents = {
  PORTFOLIO_INITIALIZED: 'portfolio_initialized',
  PORTFOLIO_PREVIEW_VIEWED: 'portfolio_preview_viewed',
  COLLECTION_CREATED: 'collection_created',
  COLLECTION_UPDATED: 'collection_updated',
  COLLECTION_DELETED: 'collection_deleted',
  JOURNAL_POST_CREATED: 'journal_post_created',
  JOURNAL_POST_UPDATED: 'journal_post_updated',
  JOURNAL_POST_DELETED: 'journal_post_deleted',
  SHOP_CATEGORY_ADDED: 'shop_category_added',
  SHOP_CATEGORY_REMOVED: 'shop_category_removed',
  CONTACT_INQUIRY_VIEWED: 'contact_inquiry_viewed',
  CONTACT_INQUIRY_REPLIED: 'contact_inquiry_replied',
  SETTINGS_UPDATED: 'settings_updated',
} as const

/**
 * Studio/Growth feature events
 */
export const StudioEvents = {
  SOCIAL_POST_GENERATED: 'social_post_generated',
  SOCIAL_POST_SCHEDULED: 'social_post_scheduled',
  SOCIAL_POST_PUBLISHED: 'social_post_published',
  DROP_CAMPAIGN_CREATED: 'drop_campaign_created',
  DROP_CAMPAIGN_LAUNCHED: 'drop_campaign_launched',
  EMAIL_CAMPAIGN_CREATED: 'email_campaign_created',
  EMAIL_CAMPAIGN_SENT: 'email_campaign_sent',
  ANALYTICS_VIEWED: 'analytics_viewed',
} as const

/**
 * Navigation/Engagement events
 */
export const NavigationEvents = {
  FEATURE_ACCESSED: 'feature_accessed',
  QUICK_ACTION_CLICKED: 'quick_action_clicked',
  EXTERNAL_LINK_CLICKED: 'external_link_clicked',
} as const

// =============================================================================
// Event Property Types
// =============================================================================

export interface UserSignedUpProperties {
  role: string
  has_artistic_name: boolean
}

export interface ArtworkUploadedProperties {
  artwork_id: number
  has_tags: boolean
  tag_count?: number
}

export interface ArtworkPublishedProperties {
  artwork_id: number
}

export interface CollectionCreatedProperties {
  collection_id: number | string
  artwork_count: number
}

export interface JournalPostCreatedProperties {
  post_id: number | string
  has_images: boolean
}

export interface PortfolioInitializedProperties {
  template?: string
  handle: string
}

export interface SocialPostGeneratedProperties {
  platform: string
  content_type: string
}

export interface FeatureAccessedProperties {
  feature_name: string
  source?: string
}

// =============================================================================
// Tracking Helper Functions
// =============================================================================

/**
 * Track an artwork upload event
 */
export function trackArtworkUploaded(
  posthog: PostHog | null,
  properties: ArtworkUploadedProperties
) {
  posthog?.capture(ArtworkEvents.UPLOADED, properties)
}

/**
 * Track an artwork publish event
 */
export function trackArtworkPublished(
  posthog: PostHog | null,
  properties: ArtworkPublishedProperties
) {
  posthog?.capture(ArtworkEvents.PUBLISHED, properties)
}

/**
 * Track a collection creation event
 */
export function trackCollectionCreated(
  posthog: PostHog | null,
  properties: CollectionCreatedProperties
) {
  posthog?.capture(EaselEvents.COLLECTION_CREATED, properties)
}

/**
 * Track a journal post creation event
 */
export function trackJournalPostCreated(
  posthog: PostHog | null,
  properties: JournalPostCreatedProperties
) {
  posthog?.capture(EaselEvents.JOURNAL_POST_CREATED, properties)
}

/**
 * Track portfolio initialization
 */
export function trackPortfolioInitialized(
  posthog: PostHog | null,
  properties: PortfolioInitializedProperties
) {
  posthog?.capture(EaselEvents.PORTFOLIO_INITIALIZED, properties)
}

/**
 * Track social content generation
 */
export function trackSocialPostGenerated(
  posthog: PostHog | null,
  properties: SocialPostGeneratedProperties
) {
  posthog?.capture(StudioEvents.SOCIAL_POST_GENERATED, properties)
}

/**
 * Track feature access for understanding user journeys
 */
export function trackFeatureAccessed(
  posthog: PostHog | null,
  properties: FeatureAccessedProperties
) {
  posthog?.capture(NavigationEvents.FEATURE_ACCESSED, properties)
}

/**
 * Track settings updates
 */
export function trackSettingsUpdated(
  posthog: PostHog | null,
  fieldsUpdated: string[]
) {
  posthog?.capture(EaselEvents.SETTINGS_UPDATED, {
    fields_updated: fieldsUpdated,
    field_count: fieldsUpdated.length,
  })
}

/**
 * Track profile updates
 */
export function trackProfileUpdated(
  posthog: PostHog | null,
  fieldsUpdated: string[]
) {
  posthog?.capture(UserEvents.PROFILE_UPDATED, {
    fields_updated: fieldsUpdated,
    field_count: fieldsUpdated.length,
  })
}

/**
 * Generic event capture with type safety
 */
export function captureEvent<T extends Record<string, unknown>>(
  posthog: PostHog | null,
  eventName: string,
  properties?: T
) {
  posthog?.capture(eventName, properties)
}
